import { resolveIcon } from '../../../../styles/icons'
import { Fragment, useMemo, useState } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import { useNavigate } from 'react-router-dom'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { KpiRow } from './Kpi'
import { relativeTime, statusOf } from './DataTableWidget'
import useAnalyticsWidget from './useAnalyticsWidget'
import useEntityRows from './useEntityRows'
import { dayKey, parseDayKey } from './period'

/*
 * What has happened, newest first: events from one or more lists merged into
 * one stream — invoices booked, payments in, quotes sent.
 *
 *     feed: {
 *         limit: 8,
 *         groupByDay: true,          Today / Yesterday / the date, as headings
 *         filters: true,             a chip per event type to narrow the stream
 *         sources: [{
 *             id: 'invoice', entity: 'invoice', filter: 'state:final', dateField: 'date',
 *             icon: <Icon_Invoice />, color: 'primary',
 *             title: 'sales.feed.invoice',       what happened
 *             subject: ['customerName'],         who or what it happened to — the first
 *                                                field with a value
 *             reference: 'number',               an identifier, after the subject
 *             amount: { field: 'totalNetSalesAmountBaseCurrency', format: 'currency' },
 *             statuses: [...],                   a badge, as on the table
 *             path: '/sales/invoice',            an event opens {path}/{id}
 *         }]
 *     }
 *
 * Each list is read for the period and cut to `limit` before merging, so the
 * merged stream holds the newest `limit` events of the period whatever their
 * mix. Two events at the same moment keep the order of their sources. Nothing
 * here is live: the stream is as fresh as its last read, and refreshes when the
 * period changes or the card is shown again.
 */

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    flex-shrink: 0;
`

const Chip = styled.button`
    font: inherit;
    ${widgetFont.caption}
    padding: 0.05rem 0.55rem;
    border-radius: 999px;
    border: 1px solid ${p => p.theme.table.border};
    background: ${p => p['aria-pressed'] ? p.theme.table.border : 'transparent'};
    color: ${p => p['aria-pressed'] ? p.theme.font.color : p.theme.font.label};
    cursor: pointer;

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

const Stream = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`

const DayHeading = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${p => p.theme.card.background};
    ${widgetFont.caption}
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${p => p.theme.font.label};
    padding: 0.35rem 0 0.2rem;
`

const Event = styled.div`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem 0.2rem;
    border-radius: 0.375rem;
    cursor: ${p => p.$clickable ? 'pointer' : 'default'};

    & + & { border-top: 1px solid ${p => p.theme.table.border}; }
    &:hover { background: ${p => p.$clickable ? p.theme.table.hover : 'transparent'}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: -2px; }
`

const IconDot = styled.span`
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    color: ${p => p.$color};
    background: color-mix(in srgb, ${p => p.$color} 14%, transparent);
`

const Text = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    ${widgetFont.body}
    line-height: 1.3;

    .title { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .subject { color: ${p => p.theme.font.label}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`

const Side = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.1rem;
    ${widgetFont.caption}
    white-space: nowrap;

    .amount { ${widgetFont.body} font-weight: 600; font-variant-numeric: tabular-nums; }
    time { color: ${p => p.theme.font.label}; }
`

const Badge = styled.span`
    padding: 0 0.45rem;
    border-radius: 999px;
    ${widgetFont.badge}
    font-weight: 600;
    background: ${p => (p.theme.emotive[p.$tone] ?? p.theme.emotive.disabled).bg};
    color: ${p => (p.theme.emotive[p.$tone] ?? p.theme.emotive.disabled).color};
`

const ActivityFeedWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const w = useAnalyticsWidget(type, config)
    const { format, locale, palette } = w
    const feed = config.feed ?? {}
    const sources = feed.sources ?? []
    const limit = feed.limit ?? 8
    const list = useEntityRows(sources.map(s => ({ ...s, limit })), w.period.range)
    const [only, setOnly] = useState(null)

    const events = useMemo(() => {
        const all = sources.flatMap((source, si) => (list.rows[si] ?? []).map((record, ri) => ({
            id: `${source.id}-${record.id ?? ri}`,
            source,
            sourceIndex: si,
            record,
            at: record[source.dateField ?? 'date'],
        })))
        return all
            .filter(e => e.at && (!only || e.source.id === only))
            .sort((a, b) => (new Date(b.at) - new Date(a.at)) || a.sourceIndex - b.sourceIndex)
            .slice(0, limit)
    }, [list.rows, sources, limit, only])

    const today = dayKey(new Date())
    const yesterday = dayKey(new Date(Date.now() - 86400000))
    const dayTitle = (key) => key === today ? t('analytics.feed.today') : key === yesterday ? t('analytics.feed.yesterday')
        : new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(parseDayKey(key))

    const open = (e) => e.source.path && e.record.id && navigate(`${e.source.path}/${e.record.id}`)

    const status = list.isLoading ? 'loading' : list.error && !events.length ? 'error' : events.length || only ? null : 'empty'

    let lastDay = null
    return (
        <AnalyticsCard
            labels={labels}
            period={w.period}
            path={config.path}
            status={status}
            error={list.error}
            onRetry={list.refetch}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            {feed.filters && sources.length > 1 && (
                <Toolbar role="group" aria-label={t('analytics.feed.filter')}>
                    <Chip type="button" aria-pressed={!only} onClick={() => setOnly(null)}>{t('analytics.feed.all')}</Chip>
                    {sources.map(s => (
                        <Chip key={s.id} type="button" aria-pressed={only === s.id} onClick={() => setOnly(s.id)}>{t(s.chip ?? s.title)}</Chip>
                    ))}
                </Toolbar>
            )}
            <Stream role="feed" aria-busy={list.isLoading}>
                {events.map(e => {
                    const s = e.source
                    const key = dayKey(new Date(e.at))
                    const heading = feed.groupByDay !== false && key !== lastDay ? dayTitle(key) : null
                    lastDay = key
                    const subject = (Array.isArray(s.subject) ? s.subject : [s.subject]).map(f => e.record[f]).find(Boolean)
                    const reference = s.reference && e.record[s.reference]
                    const badge = s.statuses && statusOf(s.statuses, e.record)
                    const color = palette.color(s.color, e.sourceIndex)
                    const clickable = !!s.path && !!e.record.id
                    return (
                        <Fragment key={e.id}>
                            {heading && <DayHeading>{heading}</DayHeading>}
                            <Event as="article" $clickable={clickable} tabIndex={clickable ? 0 : -1}
                                onClick={() => open(e)} onKeyDown={ev => ev.key === 'Enter' && open(e)}>
                                <IconDot $color={color} aria-hidden="true">{resolveIcon(s.icon)}</IconDot>
                                <Text>
                                    <span className="title">{t(s.title)}</span>
                                    <span className="subject">{[subject, reference].filter(Boolean).join(' · ') || '—'}</span>
                                </Text>
                                <Side>
                                    {s.amount && e.record[s.amount.field] != null && (
                                        <span className="amount">{format(e.record[s.amount.field], s.amount.format ?? 'currency')}</span>
                                    )}
                                    {badge && <Badge $tone={badge.tone}>{t(badge.label)}</Badge>}
                                    <time dateTime={e.at} title={new Date(e.at).toLocaleString(locale)}>{relativeTime(e.at, locale)}</time>
                                </Side>
                            </Event>
                        </Fragment>
                    )
                })}
                {!events.length && only && <DayHeading as="p">{t('analytics.state.empty')}</DayHeading>}
            </Stream>
        </AnalyticsCard>
    )
}

export default ActivityFeedWidget
